import { Box, Stack } from "@mui/material";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useEffect, useMemo, useRef } from "react";

import { TabsAtendimento } from "../TabsAtendimento";
import { TabsIntervenções } from "../TabsIntervenções";
import { TabsAssinatura } from "../TabsAssinatura";
import { EHRRecordsDialog } from "../../../ehrrecords/EHRRecordsDialog";
import { useLocation } from "react-router-dom";
import { useFormatter } from "@/utils/useFormatter";
import { toast } from "react-toastify";
import { formatarDadosParaTexto } from "@/utils/formatadorDados";
import {
  useFilteredAllergies,
  useFilteredConditions,
} from "./useFilteredMedicalData";
import { PatientQuestionnairesPanel } from "src-next/modules/patient-summary";
import { useQuestionnaires } from "@/hooks/useQuestionnaires";
import { useQuestionnairesContractContext } from "@/contexts/QuestionnairesContractContext";

export function RegisterArea() {
  const {
    activeStep,
    openSummary,
    setOpenSummary,
    handleSubmit,
    patient,
    appointment,
    summaryData,
    condutas,
    problems,
    registerAreaTab,
    luxorMode,
    isPatientQuestionnairesPanelOpen,
    setIsPatientQuestionnairesPanelOpen,
  } = useMedicalCareContext();
  const { formatGender, formatAge } = useFormatter;
  const tabsAtendimentoRef = useRef<any>(null);

  // Estado do painel de questionários agora vive no contexto, pois o botão
  // que o abre foi movido para o ActionBar.
  useEffect(() => {
    // Ao desmontar, garante que o painel não fique aberto em outras telas.
    return () => setIsPatientQuestionnairesPanelOpen(false);
  }, [setIsPatientQuestionnairesPanelOpen]);

  useEffect(() => {
    if (!isPatientQuestionnairesPanelOpen) return;
    // Mantém foco exclusivo: ao navegar no atendimento, fecha o painel
    // para não sobrepor Luxor/históricos de outras visões.
    setIsPatientQuestionnairesPanelOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, registerAreaTab, luxorMode]);

  const handleCloseSummary = () => {
    setOpenSummary(false);
  };

  const obterInformacoesPaciente = () => {
    // Extrair informações de escolaridade do paciente
    const educacaoExtension = patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel",
    );

    const escolaridade =
      educacaoExtension?.valueCodeableConcept?.coding?.[0]?.display || "-";

    return {
      idade: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
      sexo: formatGender(patient?.gender) || "-",
      escolaridade: escolaridade,
    };
  };

  const filteredConditions = useFilteredConditions();
  const filteredAllergies = useFilteredAllergies();

  const onSubmit = (data: any) => {
    // Sobrescreve os campos do data com os valores do contexto
    const dataComContexto = {
      ...data,
      New_Conduta: condutas,
      problems: problems,
    };

    const infosPaciente = obterInformacoesPaciente();
    const textoFormatado = formatarDadosParaTexto(
      dataComContexto,
      infosPaciente,
      filteredConditions,
      filteredAllergies,
    );

    navigator.clipboard
      .writeText(textoFormatado)
      .then(() => {
        toast.success("Dados de atendimento copiados com sucesso!");
        if (
          tabsAtendimentoRef.current &&
          typeof tabsAtendimentoRef.current.openLuxorModalWithContent ===
            "function"
        ) {
          tabsAtendimentoRef.current.openLuxorModalWithContent(textoFormatado);
        }
      })
      .catch(() => {
        toast.error("Erro ao copiar dados!");
      });
  };

  const { state } = useLocation();

  const {
    questionnaires: objectiveOrgQuestionnaires,
    loading: loadingObjectiveQuestionnaires,
    fetchQuestionnaires: fetchObjectiveQuestionnaires,
  } = useQuestionnaires({
    count: 50,
    status: "active",
    tag: "sleep",
  });

  const { contractQuestionnaires, loading: loadingContractQuestionnaires } =
    useQuestionnairesContractContext();

  const objectiveQuestionnairesFetchedRef = useRef(false);

  useEffect(() => {
    if (
      contractQuestionnaires &&
      !loadingContractQuestionnaires &&
      !objectiveQuestionnairesFetchedRef.current
    ) {
      objectiveQuestionnairesFetchedRef.current = true;
      fetchObjectiveQuestionnaires();
    }
    if (!contractQuestionnaires && !loadingContractQuestionnaires) {
      objectiveQuestionnairesFetchedRef.current = false;
    }
  }, [
    contractQuestionnaires,
    loadingContractQuestionnaires,
    fetchObjectiveQuestionnaires,
  ]);

  /**
   * Mesmo conjunto da aba Questionários em ObjectiveData: questionários ativos
   * com tag `sleep`, carregados quando há contrato de extensão — excluídos do
   * dialog para não duplicar o que o médico já vê em Dados objetivos.
   */
  const excludeObjectiveQuestionnaireLogicalIds = useMemo(() => {
    if (
      !contractQuestionnaires ||
      loadingContractQuestionnaires ||
      loadingObjectiveQuestionnaires
    ) {
      return [] as string[];
    }
    if (!objectiveOrgQuestionnaires?.length) return [];
    return objectiveOrgQuestionnaires
      .map((q: { id?: string }) => String(q.id ?? "").trim())
      .filter(Boolean);
  }, [
    contractQuestionnaires,
    loadingContractQuestionnaires,
    loadingObjectiveQuestionnaires,
    objectiveOrgQuestionnaires,
  ]);

  return (
    <>
      <Stack sx={{ width: "100%", height: "100%", minWidth: 0 }}>
        {/* Etapas Atendimento (0) e Intervenções (1) compartilham o mesmo
            container relativo, para que o painel de "Questionários do paciente"
            possa ser exibido como overlay em ambas as telas. */}
        {(activeStep === 0 || activeStep === 1) && (
          <Box
            sx={{ position: "relative", flex: 1, minHeight: 0, minWidth: 0 }}
          >
            <Stack sx={{ height: "100%" }}>
              {activeStep === 0 && <TabsAtendimento ref={tabsAtendimentoRef} />}
              {activeStep === 1 && <TabsIntervenções />}
            </Stack>

            {isPatientQuestionnairesPanelOpen && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: { xs: "100%", md: "44%" },
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderLeft: 1,
                  borderColor: "divider",
                  p: 2,
                  overflow: "hidden",
                }}
              >
                <PatientQuestionnairesPanel
                  summaryData={summaryData}
                  excludeQuestionnaireLogicalIds={
                    excludeObjectiveQuestionnaireLogicalIds
                  }
                />
              </Box>
            )}
          </Box>
        )}
        {/* Mantém montado (oculto fora do step 2) para DocumentVisualization
            gerar base64 antes da finalização — evita MR sem DocumentReference. */}
        <Stack
          sx={{
            display: activeStep === 2 ? "flex" : "none",
            width: "100%",
          }}
        >
          <TabsAssinatura
            patientName={state?.patientName}
            patientCPF={state?.patientId}
          />
        </Stack>

        {state?.encounterReference ? (
          <>
            <EHRRecordsDialog
              open={openSummary}
              handleClose={handleCloseSummary}
              references={state?.encounterReference}
            />
          </>
        ) : (
          <></>
        )}
      </Stack>
    </>
  );
}
